import { toaster } from '@/shared/ui/toaster.component';
import { shouldNotifyInactiveBroker } from '@/shared/utils/inactive-broker-toast.util';
import { authenticationService } from '../authentication';

export interface FetchOptions {
  method?: string;
  body?: string | FormData | Blob | ArrayBuffer | null;
  headers?: Record<string, string>;
  cache?:
    | 'default'
    | 'no-store'
    | 'reload'
    | 'no-cache'
    | 'force-cache'
    | 'only-if-cached';
  credentials?: 'omit' | 'same-origin' | 'include';
  mode?: 'cors' | 'no-cors' | 'same-origin' | 'navigate';
  redirect?: 'follow' | 'error' | 'manual';
  referrerPolicy?:
    | 'no-referrer'
    | 'no-referrer-when-downgrade'
    | 'origin'
    | 'origin-when-cross-origin'
    | 'same-origin'
    | 'strict-origin'
    | 'strict-origin-when-cross-origin'
    | 'unsafe-url';
  signal?: AbortSignal;
}

/**
 * Toast central de corretora inativa (403 "Corretora inativa."). Serve para os
 * caminhos que leem o corpo do erro por conta própria e não passam pelo
 * `handleJsonResponse` — ex.: downloads de PDF de cotação/proposta. Recebe a
 * mensagem já extraída, para ninguém ler o body duas vezes.
 */
export function notifyInactiveBroker(status: number, message: unknown): void {
  if (shouldNotifyInactiveBroker(status, message)) {
    toaster.create({ type: 'error', description: message });
  }
}

export class BaseService {
  protected baseUrl: string;

  /** Atalho de `notifyInactiveBroker` para os métodos dos services. */
  protected notifyInactiveBroker(status: number, message: unknown): void {
    notifyInactiveBroker(status, message);
  }

  constructor(baseUrl?: string) {
    this.baseUrl =
      baseUrl ||
      import.meta.env.VITE_AUTH_API_URL ||
      'https://u4x8665l4d.execute-api.us-east-1.amazonaws.com/development';
  }

  /**
   * Método base para fazer requisições HTTP com autenticação automática
   * @param path - Caminho da API (ex: '/portal/v1/identity/terms-of-use')
   * @param options - Opções adicionais do fetch
   * @param customBaseUrl - URL base customizada (opcional, sobrescreve this.baseUrl)
   * @returns Promise com a resposta da requisição
   */
  protected async fetchWithAuth(
    path: string,
    options: FetchOptions = {},
    customBaseUrl?: string
  ): Promise<Response> {
    const accessToken = await authenticationService.getAuthSession();

    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (accessToken) {
      defaultHeaders['Authorization'] = `Bearer ${accessToken.toString()}`;
    }

    const headers = {
      ...defaultHeaders,
      ...options.headers,
    };

    const baseUrl = customBaseUrl || this.baseUrl;

    const response = await fetch(`${baseUrl}${path}`, {
      ...options,
      headers,
    });

    return response;
  }

  /**
   * Método helper para fazer requisições GET
   * @param path - Caminho da API
   * @param options - Opções adicionais do fetch
   * @param customBaseUrl - URL base customizada (opcional)
   */
  protected async get(
    path: string,
    options: FetchOptions = {},
    customBaseUrl?: string
  ): Promise<Response> {
    return this.fetchWithAuth(
      path,
      { ...options, method: 'GET' },
      customBaseUrl
    );
  }

  /**
   * Método helper para fazer requisições POST
   * @param path - Caminho da API
   * @param body - Corpo da requisição
   * @param options - Opções adicionais do fetch ou URL base customizada (opcional)
   * @param customBaseUrl - URL base customizada (opcional)
   */
  protected async post(
    path: string,
    body?: unknown,
    optionsOrBaseUrl?: FetchOptions | string,
    customBaseUrl?: string
  ): Promise<Response> {
    // Suporta duas assinaturas:
    // post(path, body, options, customBaseUrl)
    // post(path, body, customBaseUrl) - quando customBaseUrl é string
    let options: FetchOptions = {};
    let baseUrl: string | undefined;

    if (typeof optionsOrBaseUrl === 'string') {
      // Terceiro parâmetro é customBaseUrl
      baseUrl = optionsOrBaseUrl;
    } else {
      // Terceiro parâmetro é options, quarto é customBaseUrl
      options = optionsOrBaseUrl || {};
      baseUrl = customBaseUrl;
    }

    return this.fetchWithAuth(
      path,
      {
        ...options,
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      },
      baseUrl
    );
  }

  /**
   * Método helper para fazer requisições PUT
   * @param path - Caminho da API
   * @param body - Corpo da requisição
   * @param options - Opções adicionais do fetch ou URL base customizada (opcional)
   * @param customBaseUrl - URL base customizada (opcional)
   */
  protected async put(
    path: string,
    body?: unknown,
    optionsOrBaseUrl?: FetchOptions | string,
    customBaseUrl?: string
  ): Promise<Response> {
    // Suporta duas assinaturas:
    // put(path, body, options, customBaseUrl)
    // put(path, body, customBaseUrl) - quando customBaseUrl é string
    let options: FetchOptions = {};
    let baseUrl: string | undefined;

    if (typeof optionsOrBaseUrl === 'string') {
      // Terceiro parâmetro é customBaseUrl
      baseUrl = optionsOrBaseUrl;
    } else {
      // Terceiro parâmetro é options, quarto é customBaseUrl
      options = optionsOrBaseUrl || {};
      baseUrl = customBaseUrl;
    }

    return this.fetchWithAuth(
      path,
      {
        ...options,
        method: 'PUT',
        body: body ? JSON.stringify(body) : undefined,
      },
      baseUrl
    );
  }

  /**
   * Método helper para fazer requisições DELETE
   * @param path - Caminho da API
   * @param options - Opções adicionais do fetch ou URL base customizada (opcional)
   * @param customBaseUrl - URL base customizada (opcional)
   */
  protected async delete(
    path: string,
    optionsOrBaseUrl?: FetchOptions | string,
    customBaseUrl?: string
  ): Promise<Response> {
    // Suporta duas assinaturas:
    // delete(path, options, customBaseUrl)
    // delete(path, customBaseUrl) - quando customBaseUrl é string
    let options: FetchOptions = {};
    let baseUrl: string | undefined;

    if (typeof optionsOrBaseUrl === 'string') {
      // Segundo parâmetro é customBaseUrl
      baseUrl = optionsOrBaseUrl;
    } else {
      // Segundo parâmetro é options, terceiro é customBaseUrl
      options = optionsOrBaseUrl || {};
      baseUrl = customBaseUrl;
    }

    return this.fetchWithAuth(path, { ...options, method: 'DELETE' }, baseUrl);
  }

  /**
   * Método helper para processar respostas JSON
   */
  protected async handleJsonResponse<T>(
    response: Response,
    options: { notifyInactiveBroker?: boolean } = {}
  ): Promise<{
    success: boolean;
    data?: T;
    error?: string;
    message?: string;
  }> {
    try {
      // Mensagens específicas do campo `errors` do ProblemDetails — aceita
      // array, objeto map (`{"0": {code, message}}`, shape do validation filter
      // da API) e mapa por campo (`{"email": ["..."]}`, do
      // `ProblemDetailsException.validationError`). São mais específicas que o
      // `detail` genérico ("Um ou mais campos estão inválidos.") e por isso têm
      // prioridade.
      const extractErrorsMessages = (errors: unknown): string | undefined => {
        if (!errors || typeof errors !== 'object') return undefined;
        const entries = (
          Array.isArray(errors)
            ? errors
            : Object.values(errors as Record<string, unknown>)
        ).flatMap(entry => (Array.isArray(entry) ? entry : [entry]));
        const messages: string[] = [];
        for (const entry of entries) {
          if (typeof entry === 'string' && entry.trim()) {
            messages.push(entry.trim());
          } else if (entry && typeof entry === 'object') {
            const msg = (entry as Record<string, unknown>).message;
            if (typeof msg === 'string' && msg.trim()) {
              messages.push(msg.trim());
            }
          }
        }
        if (messages.length === 0) return undefined;
        return [...new Set(messages)].join('; ');
      };

      const extractMessage = (data: unknown): string | undefined => {
        if (!data || typeof data !== 'object') return undefined;

        const obj = data as Record<string, unknown>;

        const fromErrors = extractErrorsMessages(obj.errors);
        if (fromErrors) return fromErrors;

        const problemDetail = typeof obj.detail === 'string' && obj.detail;
        if (problemDetail) return problemDetail;

        const direct =
          (typeof obj.message === 'string' && obj.message) ||
          (typeof obj.error === 'string' && obj.error);
        if (direct) return direct;

        const problemTitle = typeof obj.title === 'string' && obj.title;
        if (problemTitle) return problemTitle;

        return undefined;
      };

      // Suporta endpoints que retornam 204 (sem conteúdo) ou body vazio.
      if (response.status === 204) {
        return { success: true };
      }

      const text = await response.text();
      const trimmed = text.trim();

      if (!trimmed) {
        if (!response.ok) {
          return {
            success: false,
            error: `Erro ${response.status}: ${response.statusText}`,
          };
        }
        return { success: true };
      }

      let data: unknown = null;
      try {
        data = JSON.parse(trimmed);
      } catch {
        data = null;
      }

      if (!response.ok && response.status !== 304) {
        const message = extractMessage(data) || '';
        // Corretora inativa: avisa em qualquer fluxo, inclusive nos que só
        // tratam o erro inline (ex.: "Próximo" do wizard de cotação). Leituras
        // de fundo, que o usuário não pediu, desligam o aviso.
        if (options.notifyInactiveBroker !== false) {
          this.notifyInactiveBroker(response.status, message);
        }
        return {
          success: false,
          error: message || `Erro ${response.status}: ${response.statusText}`,
        };
      }

      const message = extractMessage(data);

      return {
        success: true,
        data: (data ?? undefined) as T | undefined,
        message,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao processar resposta',
      };
    }
  }
}
